import {HttpErrorResponse} from '@angular/common/http';
import {ValidationError} from '../../modules/validation/models/validation-error.model';
import {GeneralContract} from '../../models/api/product/base.model';
import {Calculation} from '../../models/api/calculation.model';
import {instanceToInstance, plainToInstance} from 'class-transformer';
import {PolicyCommunication} from '../../models/api/product/policy-communication.model';
import {GeneralContractCommunication} from '../../models/api/product/general-contract-communication.model';
import {verifyCopiedSubjects} from '../../helpers/policy-copy.helper';
import {Risk} from '../../models/api/variant/risk/risk.model';
import {clearProperties} from '../../helpers/object.helper';
import {RiskCommunication} from '../../models/api/variant/risk/risk-communication.model';
import {skipInterceptors} from '../../helpers/request.helper';
import {maxOrDefault} from '../../helpers/array.helper';
import {ParamsCommunication} from '../../models/api/variant/params/params-communication.model';

export class BaseCalculationEditState {
    protected prepareNewCalculation(generalContract: GeneralContract) {
        const calculation = plainToInstance(Calculation, {
            policy: {
                // set product code for Policy type discriminator
                productCode: generalContract.productCode,
            },
        } as Partial<Calculation>);
        // copy variant and insurer from gc
        calculation.policy.variant = instanceToInstance(generalContract.variants?.[0], {
            ignoreDecorators: true,
        });
        calculation.policy.insurer = instanceToInstance(generalContract.insurer, {ignoreDecorators: true});
        return calculation;
    }

    protected copyOriginalCalculation(
        generalContract: Readonly<GeneralContract>,
        originalCalculation: Calculation
    ): Calculation {
        const copy = instanceToInstance(originalCalculation, {ignoreDecorators: true});
        const gcVariant = generalContract.variants?.[0];
        copy.policy.variant.installments = instanceToInstance(gcVariant.installments, {ignoreDecorators: true});
        const gcRisks = instanceToInstance(gcVariant.risks, {ignoreDecorators: true});
        copy.policy.variant.risks = this.mergeExistingRisks(gcRisks, copy.policy.variant.risks);
        this.verifyCopiedNumberOfYearsInsured(
            copy.policy as PolicyCommunication,
            generalContract as GeneralContractCommunication
        );
        verifyCopiedSubjects(copy.policy as PolicyCommunication, generalContract as GeneralContractCommunication);
        if (generalContract?.insurer) {
            copy.policy.insurer = instanceToInstance(generalContract.insurer, {ignoreDecorators: true});
        }
        this.clearCopiedData(copy);
        return copy;
    }

    /**
     * search if original calculation has risks defined in GC
     */
    protected mergeExistingRisks(gcRisks: Risk[], originalRisks: Risk[]): Risk[] {
        return gcRisks.map((gcRisk) => {
            const originalRisk = originalRisks.find((item) => item.code === gcRisk.code);
            if (originalRisk) {
                originalRisk.selected = true;
                return originalRisk;
            } else {
                return gcRisk;
            }
        });
    }

    protected clearCopiedData(copy: Calculation) {
        clearProperties(copy, [
            'id',
            'number',
            'hash',
            'status',
            'installmentForms',
            'validUntil',
            'activePolicyCount',
            'soldCount',
            'vehicleClassAc',
            'vehicleClassOc',
            'isIor',
            'isIorPositive',
        ]);
        clearProperties(copy.policy as PolicyCommunication, [
            'id',
            'number',
            'premium',
            'calculationId',
            'calculationNumber',
            'isIor',
            'isIorPositive',
            'creator',
        ]);

        clearProperties(copy.policy.variant.params as ParamsCommunication, ['vehicleUsed']);

        for (const risk of copy.policy.variant.risks) {
            if ('policyNumber' in risk) {
                (risk as RiskCommunication).policyNumber = null;
            }
        }

        copy.policy.clauses = copy.policy.clauses.filter((clause) => !clause.doNotShowOnGui);
    }

    protected getManualValidation(response?: HttpErrorResponse): string | null {
        const message: string | undefined = response?.error?.message;
        if (!message) {
            return null;
        }
        // https://ebok.atena.pl/browse/EHLCUMEV-1560
        switch (response?.status) {
            case 418:
                return 'CALCULATION.CALCULATE_ERROR_REGON';
            case 404:
                // https://ebok.atena.pl/EHLCUMEV-1407
                if (message.includes('insurer')) {
                    return 'CALCULATION.NO_DATA_INSURER';
                } else if (message.includes('insured')) {
                    return 'CALCULATION.NO_DATA_INSURED';
                } else if (message.includes('vehicleUser')) {
                    return 'CALCULATION.NO_DATA_VEHICLE_USER';
                } else if (message.includes('General contract')) {
                    return 'CALCULATION.NO_DATA_GC';
                } else {
                    return null;
                }
            default:
                return null;
        }
    }

    protected getRequestOptions() {
        return {
            headers: skipInterceptors([418, 404, 409]),
        };
    }

    /**
     * filtering based on validation message arguments, which contain risk symbol (NEVO-606)
     */
    protected filterProductMessages(reject: HttpErrorResponse): ValidationError[] {
        const messages: ValidationError[] = reject.error;
        const uniqueProducts: string[] = [];
        return messages?.filter?.((message) => {
            const productSymbol = message.arguments?.[0];
            if (!productSymbol) {
                return true;
            } else if (uniqueProducts.includes(productSymbol)) {
                return false;
            } else {
                uniqueProducts.push(productSymbol);
                return true;
            }
        });
    }

    protected getValidationErrors(reject: HttpErrorResponse): ValidationError[] {
        const messages: ValidationError[] = reject.status === 409 ? this.filterProductMessages(reject) : reject.error;
        // CEVO-2661 - append policy prefix to insureds errors without it
        for (const message of messages) {
            if (message.field.includes('insureds') && !message.field.includes('policy')) {
                message.field = `policy.${message.field}`;
            }
        }
        return messages;
    }

    /**
     * mutates policy
     * CEVO-686
     */
    protected verifyCopiedNumberOfYearsInsured(
        policy: PolicyCommunication,
        generalContract: GeneralContractCommunication
    ): void {
        if (generalContract.isMultiYear && policy.numberOfYearsInsured > maxOrDefault(generalContract.lifetimeLimits)) {
            clearProperties(policy, ['start', 'stop', 'numberOfYearsInsured', 'conclusion']);
        }
    }
}
